import React, { useState, useCallback, useEffect } from 'react';
import {
  Layout,
  Card,
  Text,
  Badge,
  Button,
  Icon,
  BlockStack,
  InlineStack,
  TextField,
  ButtonGroup,
  Tooltip,
  Spinner,
} from '@shopify/polaris';
import {
  CircleTickMajor,
  ContentMinor,
  EditMajor,
  DeleteMajor,
  AddMajor,
  ChevronLeftMinor,
  ChevronRightMinor,
} from '@shopify/polaris-icons';
import { generationStoriesService } from '../services/generationStoriesService';
import { generationScenesService } from '../services/generationScenesService';
import { authService } from '../services/authService';
import AuthenticatedAssetImage from './AuthenticatedAssetImage';

// Import modal components
import CreateGenerationSceneModal from './modals/CreateGenerationSceneModal';
import EditGenerationSceneModal from './modals/EditGenerationSceneModal';
import DeleteGenerationSceneModal from './modals/DeleteGenerationSceneModal';
import RestoreGenerationSceneModal from './modals/RestoreGenerationSceneModal';
import GenerationSceneImageModal from './modals/GenerationSceneImageModal';

const SERVER_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:3001';

const GenerationScenesPage = ({ onOpenCreateStoryModal, onOpenCreateSceneModal }) => {
  // Generation Stories state
  const [generationStories, setGenerationStories] = useState([]);
  const [deletedGenerationStories, setDeletedGenerationStories] = useState([]);
  const [isCreateGenerationStoryModalOpen, setIsCreateGenerationStoryModalOpen] = useState(false);
  const [isLoadingGenerationStories, setIsLoadingGenerationStories] = useState(false);
  const [isLoadingDeletedGenerationStories, setIsLoadingDeletedGenerationStories] = useState(false);

  // Add state for Generation Story Preview modal
  const [generationStoryScenes, setGenerationStoryScenes] = useState([]);
  const [currentGenerationStoryImageIndex, setCurrentGenerationStoryImageIndex] = useState(0);
  const [isLoadingGenerationStoryScenes, setIsLoadingGenerationStoryScenes] = useState(false);

  // Generation Scenes state
  const [generationScenes, setGenerationScenes] = useState([]);
  const [deletedGenerationScenes, setDeletedGenerationScenes] = useState([]);
  const [generationScenesTab, setGenerationScenesTab] = useState('all');
  const [generationScenesSearchQuery, setGenerationScenesSearchQuery] = useState('');
  const [selectedGenerationScene, setSelectedGenerationScene] = useState(null);
  const [isEditGenerationSceneModalOpen, setIsEditGenerationSceneModalOpen] = useState(false);
  const [isDeleteGenerationSceneModalOpen, setIsDeleteGenerationSceneModalOpen] = useState(false);
  const [isRestoreGenerationSceneModalOpen, setIsRestoreGenerationSceneModalOpen] = useState(false);
  const [isCreateGenerationSceneModalOpen, setIsCreateGenerationSceneModalOpen] = useState(false);
  const [isImageGenerationModalOpen, setIsImageGenerationModalOpen] = useState(false);
  const [selectedImageGenerationUrl, setSelectedImageGenerationUrl] = useState('');
  const [selectedImageGenerationAlt, setSelectedImageGenerationAlt] = useState('');
  const [isDeletingGenerationScene, setIsDeletingGenerationScene] = useState(false);
  const [isRestoringGenerationScene, setIsRestoringGenerationScene] = useState(false);
  const [deleteGenerationSceneId, setDeleteGenerationSceneId] = useState('');
  const [restoreGenerationSceneId, setRestoreGenerationSceneId] = useState('');
  const [isLoadingGenerationScenes, setIsLoadingGenerationScenes] = useState(false);
  const [isLoadingDeletedGenerationScenes, setIsLoadingDeletedGenerationScenes] = useState(false);

  // Pagination for generation scenes
  const [generationScenesPage, setGenerationScenesPage] = useState(1);
  const GENERATION_SCENES_PER_PAGE = 50;
  const paginatedGenerationScenes = (
    generationScenesTab === 'all' ? generationScenes :
    generationScenesTab === 'deleted' ? deletedGenerationScenes :
    generationScenesTab === 'blurred' ? generationScenes.filter(scene => scene.blurred) :
    generationScenesTab === 'clear' ? generationScenes.filter(scene => !scene.blurred) :
    generationScenes
  ).filter(scene => {
    const q = generationScenesSearchQuery.toLowerCase();
    return (
      (scene.text && scene.text.toLowerCase().includes(q)) ||
      (scene.type && scene.type.toLowerCase().includes(q)) ||
      (scene.slug && scene.slug.toLowerCase().includes(q)) ||
      (scene.story_id && generationStories.find(s => s._id === scene.story_id)?.title && generationStories.find(s => s._id === scene.story_id).title.toLowerCase().includes(q))
    );
  });
  const totalGenerationScenesPages = Math.ceil(paginatedGenerationScenes.length / GENERATION_SCENES_PER_PAGE);
  const generationScenesToShow = paginatedGenerationScenes.slice((generationScenesPage - 1) * GENERATION_SCENES_PER_PAGE, generationScenesPage * GENERATION_SCENES_PER_PAGE);

  // Helper function to generate slug from title
  const generateSlug = (title) => {
    if (!title) return '';
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  // Fetch all stories
  const fetchGenerationStories = useCallback(async () => {
    setIsLoadingGenerationStories(true);
    try {
      const data = await generationStoriesService.getAllStories();
      setGenerationStories(Array.isArray(data) ? data : []);
    } catch (err) {
      setGenerationStories([]);
    } finally {
      setIsLoadingGenerationStories(false);
    }
  }, []);

  // Fetch deleted stories
  const fetchDeletedGenerationStories = useCallback(async () => {
    setIsLoadingDeletedGenerationStories(true);
    try {
      const data = await generationStoriesService.getDeletedStories();
      setDeletedGenerationStories(Array.isArray(data) ? data : []);
    } catch (err) {
      setDeletedGenerationStories([]);
    } finally {
      setIsLoadingDeletedGenerationStories(false);
    }
  }, []);



  // Generation Scenes fetch logic
  const fetchGenerationScenes = useCallback(async () => {
    setIsLoadingGenerationScenes(true);
    try {
      const data = await generationScenesService.getAllScenes();
      setGenerationScenes(Array.isArray(data) ? data : []);
    } catch (err) {
      setGenerationScenes([]);
    } finally {
      setIsLoadingGenerationScenes(false);
    }
  }, []);

  const fetchDeletedGenerationScenes = useCallback(async () => {
    setIsLoadingDeletedGenerationScenes(true);
    try {
      const data = await generationScenesService.getDeletedScenes();
      setDeletedGenerationScenes(Array.isArray(data) ? data : []);
    } catch (err) {
      setDeletedGenerationScenes([]);
    } finally {
      setIsLoadingDeletedGenerationScenes(false);
    }
  }, []);



  const handleDeleteGenerationScene = useCallback((id) => {
    setDeleteGenerationSceneId(id);
    setIsDeleteGenerationSceneModalOpen(true);
  }, []);

  const handleConfirmDeleteGenerationScene = useCallback(async () => {
    if (!deleteGenerationSceneId) return;
    setIsDeletingGenerationScene(true);
    try {
      await generationScenesService.deleteScene(deleteGenerationSceneId);
      fetchGenerationScenes();
      setIsDeleteGenerationSceneModalOpen(false);
      setDeleteGenerationSceneId('');
    } catch (err) {
      // handle error
    } finally {
      setIsDeletingGenerationScene(false);
    }
  }, [deleteGenerationSceneId, fetchGenerationScenes]);

  const handleRestoreGenerationScene = useCallback((id) => {
    setRestoreGenerationSceneId(id);
    setIsRestoreGenerationSceneModalOpen(true);
  }, []);

  const handleConfirmRestoreGenerationScene = useCallback(async () => {
    if (!restoreGenerationSceneId) return;
    setIsRestoringGenerationScene(true);
    try {
      await generationScenesService.restoreScene(restoreGenerationSceneId);
      fetchDeletedGenerationScenes();
      fetchGenerationScenes();
      setIsRestoreGenerationSceneModalOpen(false);
      setRestoreGenerationSceneId('');
    } catch (err) {
      // handle error
    } finally {
      setIsRestoringGenerationScene(false);
    }
  }, [restoreGenerationSceneId, fetchDeletedGenerationScenes, fetchGenerationScenes]);

  const handleImageClick = useCallback((imageUrl, imageAlt) => {
    setSelectedImageGenerationUrl(imageUrl);
    setSelectedImageGenerationAlt(imageAlt);
    setIsImageGenerationModalOpen(true);
  }, []);

  const handleEditGenerationScene = useCallback((scene) => {
    setSelectedGenerationScene(scene);
    setIsEditGenerationSceneModalOpen(true);
  }, []);



  // Fetch scenes when tab changes
  useEffect(() => {
    fetchGenerationStories(); // Always fetch stories when entering Scenes tab
      if (generationScenesTab === 'all') {
        fetchGenerationScenes();
      } else if (generationScenesTab === 'deleted') {
        fetchDeletedGenerationScenes();
      }
  }, [generationScenesTab, fetchGenerationScenes, fetchDeletedGenerationScenes, fetchGenerationStories]);

  // Listen for modal opening events from primary action buttons
  useEffect(() => {
    const handleOpenCreateStoryModal = () => {
      setIsCreateGenerationStoryModalOpen(true);
    };

    const handleOpenCreateSceneModal = () => {
      setIsCreateGenerationSceneModalOpen(true);
    };

    window.addEventListener('openCreateGenerationStoryModal', handleOpenCreateStoryModal);
    window.addEventListener('openCreateGenerationSceneModal', handleOpenCreateSceneModal);

    return () => {
      window.removeEventListener('openCreateGenerationStoryModal', handleOpenCreateStoryModal);
      window.removeEventListener('openCreateGenerationSceneModal', handleOpenCreateSceneModal);
    };
  }, []);

  const fetchGenerationStoryScenes = useCallback(async (slug) => {
    setIsLoadingGenerationStoryScenes(true);
    try {
      const data = await generationScenesService.getScenesByStorySlug(slug);
      setGenerationStoryScenes(Array.isArray(data) ? data : []);
      setCurrentGenerationStoryImageIndex(0);
    } catch (err) {
      setGenerationStoryScenes([]);
    } finally {
      setIsLoadingGenerationStoryScenes(false);
    }
  }, []);

  return (
    <Layout>
      <Layout.Section>
        <Card>
          {/* Filter/Status Tabs and Search Bar Row */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 16px 0 16px' }}>
            {/* Tabs */}
            <div style={{ display: 'flex', gap: 8 }}>
              <ButtonGroup>
                <Button 
                  onClick={() => setGenerationScenesTab('all')}
                  variant="secondary"
                  tone={generationScenesTab === 'all' ? 'success' : undefined}
                >
                  All
                </Button>
                <Button 
                  onClick={() => setGenerationScenesTab('blurred')}
                  variant="secondary"
                  tone={generationScenesTab === 'blurred' ? 'success' : undefined}
                >
                  Blurred
                </Button>
                <Button 
                  onClick={() => setGenerationScenesTab('clear')}
                  variant="secondary"
                  tone={generationScenesTab === 'clear' ? 'success' : undefined}
                >
                  Clear
                </Button>
                <Button 
                  onClick={() => setGenerationScenesTab('deleted')}
                  variant="secondary"
                  tone={generationScenesTab === 'deleted' ? 'success' : undefined}
                >
                  Deleted
                </Button>
              </ButtonGroup>
            </div>
            {/* Search Bar */}
            {(generationScenesTab === 'all' ? generationScenes : deletedGenerationScenes).length > 0 && (
              <div style={{ minWidth: 260 }}>
                <TextField
                  label="Search by title, type, slug, or story..."
                  value={generationScenesSearchQuery}
                  onChange={setGenerationScenesSearchQuery}
                  placeholder="Search by title, type, slug, or story..."
                  autoComplete="off"
                  prefix={
                    <span className="Polaris-Icon">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="9" cy="9" r="7.5" stroke="var(--s-icon-color-25041, rgba(74, 74, 74, 1))" strokeWidth="1.5" />
                        <path d="M15 15L18 18" stroke="var(--s-icon-color-25041, rgba(74, 74, 74, 1))" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                    </span>
                  }
                  labelHidden
                />
              </div>
            )}
          </div>
          <div style={{ overflowX: 'auto', marginTop: 16 }}>
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
              {(generationScenesTab === 'all' ? generationScenes : deletedGenerationScenes).filter(scene => {
                const q = generationScenesSearchQuery.toLowerCase();
                return (
                  (scene.text && scene.text.toLowerCase().includes(q)) ||
                  (scene.type && scene.type.toLowerCase().includes(q)) ||
                  (scene.slug && scene.slug.toLowerCase().includes(q)) ||
                  (scene.story_id && generationStories.find(s => s._id === scene.story_id)?.title && generationStories.find(s => s._id === scene.story_id).title.toLowerCase().includes(q))
                );
              }).length > 0 && (
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '12px 8px', fontWeight: 600, color: '#6d7175', fontSize: 13, background: '#fafbfc', borderBottom: '1px solid #e3e3e3', minWidth: 56 }}></th>
                    <th style={{ textAlign: 'center', padding: '12px 8px', fontWeight: 600, color: '#6d7175', fontSize: 13, background: '#fafbfc', borderBottom: '1px solid #e3e3e3', minWidth: 80 }}>Story</th>
                    <th style={{ textAlign: 'center', padding: '12px 8px', fontWeight: 600, color: '#6d7175', fontSize: 13, background: '#fafbfc', borderBottom: '1px solid #e3e3e3', minWidth: 80 }}>Slug</th>
                    <th style={{ textAlign: 'center', padding: '12px 8px', fontWeight: 600, color: '#6d7175', fontSize: 13, background: '#fafbfc', borderBottom: '1px solid #e3e3e3', minWidth: 80 }}>Type</th>
                    <th style={{ textAlign: 'center', padding: '12px 8px', fontWeight: 600, color: '#6d7175', fontSize: 13, background: '#fafbfc', borderBottom: '1px solid #e3e3e3', minWidth: 80 }}>Text</th>
                    <th style={{ textAlign: 'center', padding: '12px 8px', fontWeight: 600, color: '#6d7175', fontSize: 13, background: '#fafbfc', borderBottom: '1px solid #e3e3e3', minWidth: 80 }}>Status</th>
                    <th style={{ textAlign: 'center', padding: '12px 8px', fontWeight: 600, color: '#6d7175', fontSize: 13, background: '#fafbfc', borderBottom: '1px solid #e3e3e3', minWidth: 80 }}>Actions</th>
                  </tr>
                </thead>
              )}
              <tbody>
                {isLoadingGenerationScenes || isLoadingDeletedGenerationScenes ? (
                  <tr>
                    <td colSpan="7" style={{ padding: '60px 16px', textAlign: 'center' }}>
                      <BlockStack gap="400" align="center">
                        <Spinner size="large" />
                        <Text variant="bodyMd" tone="subdued">Loading scenes...</Text>
                      </BlockStack>
                    </td>
                  </tr>
                ) : (generationScenesTab === 'all' ? generationScenes : deletedGenerationScenes).filter(scene => {
                  const q = generationScenesSearchQuery.toLowerCase();
                  return (
                    (scene.text && scene.text.toLowerCase().includes(q)) ||
                    (scene.type && scene.type.toLowerCase().includes(q)) ||
                    (scene.slug && scene.slug.toLowerCase().includes(q)) ||
                    (scene.story_id && generationStories.find(s => s._id === scene.story_id)?.title && generationStories.find(s => s._id === scene.story_id).title.toLowerCase().includes(q))
                  );
                }).length > 0 ? (
                  generationScenesToShow.map(scene => {
                    const storyTitle = generationStories.find(s => s._id === scene.story_id)?.title || 'Unknown Story';
                    return (
                      <tr key={scene._id || scene.slug} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '12px 8px', textAlign: 'left' }}>
                          <div 
                            style={{ 
                              width: 48, 
                              height: 48, 
                              borderRadius: 8, 
                              overflow: 'hidden', 
                              background: '#f6f6f7', 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              cursor: 'pointer',
                              transition: 'transform 0.2s ease-in-out'
                            }}
                            onClick={() => handleImageClick(
                              scene.scene_url || 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-image.png',
                              scene.text || 'Scene'
                            )}
                            onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                            onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                          >
                            <AuthenticatedAssetImage
                              src={scene.scene_url || 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-image.png'} 
                              assetSize="thumbnail"
                              alt={scene.text || 'Scene'} 
                              style={{ width: 48, height: 48, objectFit: 'cover' }} 
                            />
                          </div>
                        </td>
                        <td style={{ padding: '12px 8px', minWidth: 100, color: '#6d7175', fontSize: 13, textAlign: 'center' }}>{storyTitle}</td>
                        <td style={{ padding: '12px 8px', minWidth: 120, color: '#6d7175', fontSize: 13, textAlign: 'center' }}>{scene.slug && scene.slug.trim() ? scene.slug : ''}</td>
                        <td style={{ padding: '12px 8px', minWidth: 100, color: '#6d7175', fontSize: 13, textAlign: 'center' }}>{scene.type && scene.type.trim() ? scene.type : ''}</td>
                        <td style={{ padding: '12px 8px', maxWidth: 90 }}>
                          <div>
                            {scene.text && scene.text.trim() ? (
                              <Text variant="bodyMd">{scene.text}</Text>
                            ) : null}
                          </div>
                        </td>
                        <td style={{ padding: '12px 8px', minWidth: 120, textAlign: 'center' }}>
                          {scene.blurred ? (
                            <Badge tone="warning">Blurred</Badge>
                          ) : (
                            <Badge tone="success">Clear</Badge>
                          )}
                        </td>
                        <td style={{ padding: '12px 8px', minWidth: 120, textAlign: 'center' }}>
                          <InlineStack gap="100" align="center">
                            {generationScenesTab === 'all' ? (
                              <>
                                <Tooltip content="Edit">
                                  <Button 
                                    icon={EditMajor} 
                                    size="slim" 
                                    onClick={() => handleEditGenerationScene(scene)}
                                  />
                                </Tooltip>
                                <Tooltip content="Delete">
                                  <Button 
                                    icon={DeleteMajor} 
                                    size="slim" 
                                    onClick={() => handleDeleteGenerationScene(scene._id)}
                                  />
                                </Tooltip>
                              </>
                            ) : (
                              <Tooltip content="Restore">
                                <Button 
                                  icon={CircleTickMajor} 
                                  size="slim" 
                                  onClick={() => handleRestoreGenerationScene(scene._id)}
                                />
                              </Tooltip>
                            )}
                          </InlineStack>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="7" style={{ padding: '60px 16px', textAlign: 'center' }}>
                      <BlockStack gap="400" align="center">
                        <Icon 
                          source={ContentMinor} 
                          color="subdued" 
                          className="empty-state-big-icon"
                        />
                        <Text variant="bodyMd" tone="subdued">No records found</Text>
                        {generationScenesTab === 'all' && (
                          <div style={{ display: 'inline-block' }}>
                            <Button 
                              icon={AddMajor} 
                              onClick={() => setIsCreateGenerationSceneModalOpen(true)}
                              variant="primary"
                              size="large"
                            >
                              Add Scene
                            </Button>
                          </div>
                        )}
                      </BlockStack>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {/* Segmented Pagination Controls */}
          {totalGenerationScenesPages > 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '24px 0' }}>
              <ButtonGroup variant="segmented">
                <Button
                  icon={ChevronLeftMinor}
                  disabled={generationScenesPage === 1}
                  onClick={() => setGenerationScenesPage(generationScenesPage - 1)}
                />
                {(() => {
                  let start = Math.max(1, generationScenesPage - 2);
                  let end = Math.min(totalGenerationScenesPages, start + 4);
                  if (end - start < 4) start = Math.max(1, end - 4);
                  const pages = [];
                  for (let i = start; i <= end; i++) {
                    pages.push(
                      <Button
                        key={i}
                        pressed={i === generationScenesPage}
                        onClick={() => setGenerationScenesPage(i)}
                        variant={i === generationScenesPage ? 'secondary' : 'secondary'}
                      >
                        {i}
                      </Button>
                    );
                  }
                  return pages;
                })()}
                <Button
                  icon={ChevronRightMinor}
                  disabled={generationScenesPage === totalGenerationScenesPages}
                  onClick={() => setGenerationScenesPage(generationScenesPage + 1)}
                />
              </ButtonGroup>
              <div style={{ marginTop: 8, marginBottom: 16, color: '#6d7175', fontSize: 13 }}>
                {((generationScenesPage - 1) * GENERATION_SCENES_PER_PAGE) + 1}
                –
                {Math.min(generationScenesPage * GENERATION_SCENES_PER_PAGE, paginatedGenerationScenes.length)}
                {' of '}
                {paginatedGenerationScenes.length}
                {' (Page '}{generationScenesPage}{' of '}{totalGenerationScenesPages}{')'}
              </div>
            </div>
          )}
        </Card>

        {/* Create Generation Scene Modal */}
        <CreateGenerationSceneModal
          isOpen={isCreateGenerationSceneModalOpen}
          onClose={() => setIsCreateGenerationSceneModalOpen(false)}
          onSceneCreated={fetchGenerationScenes}
          generationStories={generationStories}
          isLoadingGenerationStories={isLoadingGenerationStories}
        />

        {/* Edit Generation Scene Modal */}
        <EditGenerationSceneModal
          isOpen={isEditGenerationSceneModalOpen}
          onClose={() => setIsEditGenerationSceneModalOpen(false)}
          selectedScene={selectedGenerationScene}
          onSceneUpdated={fetchGenerationScenes}
          generationStories={generationStories}
          isLoadingGenerationStories={isLoadingGenerationStories}
        />

        {/* Delete Generation Scene Modal */}
        <DeleteGenerationSceneModal
          isOpen={isDeleteGenerationSceneModalOpen}
          onClose={() => setIsDeleteGenerationSceneModalOpen(false)}
          onConfirm={handleConfirmDeleteGenerationScene}
          isDeleting={isDeletingGenerationScene}
        />

        {/* Restore Generation Scene Modal */}
        <RestoreGenerationSceneModal
          isOpen={isRestoreGenerationSceneModalOpen}
          onClose={() => setIsRestoreGenerationSceneModalOpen(false)}
          onConfirm={handleConfirmRestoreGenerationScene}
          isRestoring={isRestoringGenerationScene}
        />

        {/* Generation Scene Image Modal */}
        <GenerationSceneImageModal
          isOpen={isImageGenerationModalOpen}
          onClose={() => setIsImageGenerationModalOpen(false)}
          imageUrl={selectedImageGenerationUrl}
          imageAlt={selectedImageGenerationAlt}
        />
      </Layout.Section>
    </Layout>
  );
};

export default GenerationScenesPage; 